import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  type ColorRepresentation,
} from "three";
import i18next from "i18next";
import type { ModelData, PlacementType } from "../../api/types/Product";
import { DEFAULT_ROOM_SURFACE_COLORS } from "../segment/RoomStyle";

/**
 * Must match `DEFAULT_WALL_HEIGHT` in `PlanarGraph3DView` (no room-height API yet).
 * Columns always use this as full ceiling height. Beams hang with top at this height.
 */
export const ROOM_WALL_HEIGHT_M = 2.7;

export type StructuralKind =
  | "rect-column"
  | "round-column"
  | "beam"
  | "radiator"
  | "pool";

const STRUCTURAL_KIND_SET = new Set<StructuralKind>([
  "rect-column",
  "round-column",
  "beam",
  "radiator",
  "pool",
]);

/** FLOOR structural items that never wall-snap (push-out / free place only). */
export function isStructuralFloorFreeKind(kind: StructuralKind | string | undefined): boolean {
  return (
    kind === "rect-column" ||
    kind === "round-column" ||
    kind === "beam" ||
    kind === "pool"
  );
}

/** Items with Yatay/Dikey + elevation (world Y) — beam, pool. */
export function isStructuralElevatableKind(
  kind: StructuralKind | string | undefined | null,
): boolean {
  return kind === "beam" || kind === "pool";
}

/** Pool may sink below the floor (negative world Y). Beams stay ≥ 0. */
export function allowsNegativeStructuralElevation(
  kind: StructuralKind | string | undefined | null,
): boolean {
  return kind === "pool";
}

/** Beam drag: XZ floor plane vs Y-only elevation. */
export type BeamMoveMode = "horizontal" | "vertical";

export type StructuralSizeM = {
  /** Footprint X / span length for beams (m). */
  width: number;
  /** Footprint Z / beam thickness (m). */
  depth: number;
  /** Vertical size (m). Columns ignore this and use {@link ROOM_WALL_HEIGHT_M}. */
  height: number;
};

export type StructuralItemDef = {
  kind: StructuralKind;
  id: string;
  placeType: PlacementType;
  /** Rotation via float menu allowed. */
  canRotate: boolean;
  /** Footprint resize axes; columns keep height locked to room. */
  resizeMode: "box" | "diameter" | "wall-box";
  defaultSize: StructuralSizeM;
  color: ColorRepresentation;
  thumbnailSvg: string;
};

const ID_PREFIX = "structural:";

/** Columns + beam follow wall paint; radiator keeps its metal finish. */
export function structuralFollowsWallColor(kind: StructuralKind): boolean {
  return (
    kind === "rect-column" ||
    kind === "round-column" ||
    kind === "beam"
  );
}

export function applyStructuralWallColor(
  root: Object3D,
  color: ColorRepresentation,
): void {
  root.traverse((obj) => {
    if (!(obj instanceof Mesh)) return;
    const materials = Array.isArray(obj.material)
      ? obj.material
      : [obj.material];
    for (const mat of materials) {
      if (mat instanceof MeshStandardMaterial) {
        mat.color.set(color);
      }
    }
  });
}

export function structuralId(kind: StructuralKind): string {
  return `${ID_PREFIX}${kind}`;
}

export function parseStructuralKind(id: unknown): StructuralKind | null {
  if (typeof id !== "string" || !id.startsWith(ID_PREFIX)) return null;
  const kind = id.slice(ID_PREFIX.length) as StructuralKind;
  return STRUCTURAL_KIND_SET.has(kind) ? kind : null;
}

export function isStructuralId(id: unknown): boolean {
  return parseStructuralKind(id) != null;
}

export function isStructuralModelData(
  data: ModelData | undefined | null,
): data is ModelData & { structuralKind: StructuralKind } {
  const kind = (data as { structuralKind?: unknown } | undefined)
    ?.structuralKind;
  return typeof kind === "string" && STRUCTURAL_KIND_SET.has(kind as StructuralKind);
}

/** Catalog placeType wins for structural items (saved WALL radiators stay dual-surface). */
export function placeTypeOfModelData(
  data: ModelData | undefined | null,
): PlacementType | null | undefined {
  if (isStructuralModelData(data)) {
    return structuralItemByKind(data.structuralKind).placeType;
  }
  return data?.placeType;
}

function svgDataUrl(body: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">${body}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const THUMB_BG = `<rect width="64" height="64" rx="10" fill="#f1f5f9"/>`;

/** Soft filled preview cards for the Yapı grid (not monoline chrome icons). */
const THUMB_RECT_COLUMN = svgDataUrl(`
  ${THUMB_BG}
  <defs>
    <linearGradient id="rc" x1="18" y1="8" x2="46" y2="56" gradientUnits="userSpaceOnUse">
      <stop stop-color="#e8e6e1"/>
      <stop offset="1" stop-color="#b0aca4"/>
    </linearGradient>
  </defs>
  <rect x="20" y="8" width="24" height="48" rx="2.5" fill="url(#rc)" stroke="#8a8680" stroke-width="1.25"/>
  <rect x="20" y="8" width="24" height="5" rx="2.5" fill="#d4d1cb" stroke="#8a8680" stroke-width="1.25"/>
  <rect x="20" y="51" width="24" height="5" rx="2.5" fill="#d4d1cb" stroke="#8a8680" stroke-width="1.25"/>
  <path d="M24 16v32M40 16v32" stroke="#8a8680" stroke-width="1" opacity=".35"/>
`);

const THUMB_ROUND_COLUMN = svgDataUrl(`
  ${THUMB_BG}
  <defs>
    <linearGradient id="oc" x1="20" y1="8" x2="44" y2="56" gradientUnits="userSpaceOnUse">
      <stop stop-color="#ebe8e2"/>
      <stop offset=".45" stop-color="#c2bfb7"/>
      <stop offset="1" stop-color="#9e9a92"/>
    </linearGradient>
  </defs>
  <ellipse cx="32" cy="12" rx="11" ry="4.5" fill="#d8d5cf" stroke="#8a8680" stroke-width="1.25"/>
  <path d="M21 12v36c0 2.5 4.9 4.5 11 4.5s11-2 11-4.5V12" fill="url(#oc)" stroke="#8a8680" stroke-width="1.25"/>
  <ellipse cx="32" cy="12" rx="11" ry="4.5" fill="#e4e1db" stroke="#8a8680" stroke-width="1.25"/>
  <ellipse cx="32" cy="48" rx="11" ry="4.5" fill="#b8b4ac" stroke="#8a8680" stroke-width="1.25"/>
`);

const THUMB_BEAM = svgDataUrl(`
  ${THUMB_BG}
  <defs>
    <linearGradient id="bm" x1="8" y1="22" x2="56" y2="38" gradientUnits="userSpaceOnUse">
      <stop stop-color="#ebe8e2"/>
      <stop offset="1" stop-color="#b0aca4"/>
    </linearGradient>
  </defs>
  <rect x="10" y="14" width="44" height="3" rx="1" fill="#d4d1cb" stroke="#8a8680" stroke-width="1"/>
  <rect x="8" y="22" width="48" height="14" rx="2.5" fill="url(#bm)" stroke="#8a8680" stroke-width="1.25"/>
  <path d="M12 26h40M12 32h40" stroke="#8a8680" stroke-width="1" opacity=".3"/>
  <rect x="8" y="22" width="4" height="14" rx="1" fill="#d8d5cf" stroke="#8a8680" stroke-width="1"/>
  <rect x="52" y="22" width="4" height="14" rx="1" fill="#d8d5cf" stroke="#8a8680" stroke-width="1"/>
`);

const THUMB_RADIATOR = svgDataUrl(`
  ${THUMB_BG}
  <rect x="8" y="18" width="48" height="28" rx="3" fill="#eef1f4" stroke="#94a3b8" stroke-width="1.25"/>
  <rect x="8" y="18" width="48" height="4" rx="2" fill="#dbe2ea" stroke="#94a3b8" stroke-width="1"/>
  <rect x="8" y="42" width="48" height="4" rx="2" fill="#dbe2ea" stroke="#94a3b8" stroke-width="1"/>
  <g stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round">
    <path d="M14 24v16M19 24v16M24 24v16M29 24v16M34 24v16M39 24v16M44 24v16M49 24v16"/>
  </g>
`);

const THUMB_POOL = svgDataUrl(`
  ${THUMB_BG}
  <rect x="8" y="18" width="48" height="30" rx="4" fill="#c5d4de" stroke="#64748b" stroke-width="1.25"/>
  <rect x="12" y="22" width="40" height="22" rx="3" fill="#5eb0d8" stroke="#3b82a8" stroke-width="1"/>
  <path d="M16 30c4 3 8 3 12 0s8-3 12 0 8 3 12 0" stroke="#a8ddf0" stroke-width="1.5" fill="none" opacity=".7"/>
`);

const WALL_PAINT = DEFAULT_ROOM_SURFACE_COLORS.wall;

/**
 * Catalog of placeable structural objects (procedural meshes — no GLB).
 *
 * Radiator height assumption: typical panel ~60 cm tall (user specified width/
 * depth only). May sit on a wall or on the floor (`FLOORANDWALL`).
 * Beam: horizontal box; initial place hangs under the ceiling (top at room
 * height) via `position.y`, then elevation is adjustable.
 */
export const STRUCTURAL_ITEMS: readonly StructuralItemDef[] = [
  {
    kind: "rect-column",
    id: structuralId("rect-column"),
    placeType: "FLOOR",
    canRotate: true,
    resizeMode: "box",
    // 40×40 cm footprint; height = room height
    defaultSize: { width: 0.4, depth: 0.4, height: ROOM_WALL_HEIGHT_M },
    color: WALL_PAINT,
    thumbnailSvg: THUMB_RECT_COLUMN,
  },
  {
    kind: "round-column",
    id: structuralId("round-column"),
    placeType: "FLOOR",
    canRotate: true,
    resizeMode: "diameter",
    // 40 cm diameter; height = room height
    defaultSize: { width: 0.4, depth: 0.4, height: ROOM_WALL_HEIGHT_M },
    color: WALL_PAINT,
    thumbnailSvg: THUMB_ROUND_COLUMN,
  },
  {
    kind: "beam",
    id: structuralId("beam"),
    placeType: "FLOOR",
    canRotate: true,
    resizeMode: "box",
    // Span 2.5 m × 25 cm thick × 30 cm deep; hangs at ceiling.
    defaultSize: { width: 2.5, depth: 0.25, height: 0.3 },
    color: WALL_PAINT,
    thumbnailSvg: THUMB_BEAM,
  },
  {
    kind: "radiator",
    id: structuralId("radiator"),
    placeType: "FLOORANDWALL",
    canRotate: false,
    resizeMode: "wall-box",
    // Spec: en 100 cm along wall, depth/genişlik 15 cm from wall; height ~60 cm.
    defaultSize: { width: 1.0, depth: 0.15, height: 0.6 },
    color: 0xe8ecef,
    thumbnailSvg: THUMB_RADIATOR,
  },
  {
    kind: "pool",
    id: structuralId("pool"),
    placeType: "FLOOR",
    canRotate: true,
    resizeMode: "box",
    // Outdoor pool basin ~3×2 m, ~45 cm deep shell.
    defaultSize: { width: 3.0, depth: 2.0, height: 0.45 },
    color: 0x5eb0d8,
    thumbnailSvg: THUMB_POOL,
  },
];

export function structuralItemByKind(
  kind: StructuralKind,
): StructuralItemDef {
  return STRUCTURAL_ITEMS.find((item) => item.kind === kind)!;
}

export function structuralDisplayName(kind: StructuralKind): string {
  // Must be a plain string (i18next.t). lit-i18n's `t()` returns a Lit
  // directive — GridPane calls `.toLowerCase()` on item.name and would throw.
  switch (kind) {
    case "rect-column":
      return i18next.t("structural.rectColumn");
    case "round-column":
      return i18next.t("structural.roundColumn");
    case "beam":
      return i18next.t("structural.beam");
    case "radiator":
      return i18next.t("structural.radiator");
    case "pool":
      return i18next.t("structural.pool");
  }
}

export function createStructuralModelData(kind: StructuralKind): ModelData {
  const def = structuralItemByKind(kind);
  const size = { ...def.defaultSize };
  if (kind === "rect-column" || kind === "round-column") {
    size.height = ROOM_WALL_HEIGHT_M;
  }
  return {
    id: def.id as unknown as number,
    name: structuralDisplayName(kind),
    gltf: "",
    placeType: def.placeType,
    // SugarModelDimensions treats x/y/z as centimeters.
    x: Math.round(size.width * 100),
    y: Math.round(size.height * 100),
    z: Math.round(size.depth * 100),
    structuralKind: kind,
    structuralSize: size,
    canRotate: def.canRotate,
    resizeMode: def.resizeMode,
  } as ModelData;
}

function isColumnKind(kind: StructuralKind | undefined): boolean {
  return kind === "rect-column" || kind === "round-column";
}

export function sizeFromStructuralData(data: ModelData): StructuralSizeM {
  const stored = (data as { structuralSize?: StructuralSizeM }).structuralSize;
  const kind = (data as { structuralKind?: StructuralKind }).structuralKind;
  if (stored) {
    return {
      width: stored.width,
      depth: stored.depth,
      height: isColumnKind(kind) ? ROOM_WALL_HEIGHT_M : stored.height,
    };
  }
  const fallback = kind
    ? structuralItemByKind(kind).defaultSize
    : { width: 0.4, depth: 0.4, height: ROOM_WALL_HEIGHT_M };
  return { ...fallback };
}

function makeConcreteMaterial(color: ColorRepresentation): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color,
    roughness: 0.9,
    metalness: 0.02,
  });
}

function makeMetalMaterial(color: ColorRepresentation): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color,
    roughness: 0.38,
    metalness: 0.55,
  });
}

function makeWaterMaterial(color: ColorRepresentation): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color,
    roughness: 0.18,
    metalness: 0.12,
    transparent: true,
    opacity: 0.82,
  });
}

function addRectColumn(
  group: Group,
  size: StructuralSizeM,
  color: ColorRepresentation,
): void {
  const height = ROOM_WALL_HEIGHT_M;
  const { width, depth } = size;
  const mat = makeConcreteMaterial(color);
  const baseH = Math.min(0.07, height * 0.028);
  const inset = 0.92;

  const base = new BoxGeometry(width, baseH, depth);
  base.translate(0, baseH * 0.5, 0);
  group.add(new Mesh(base, mat));

  const shaftH = height - baseH * 2;
  const shaft = new BoxGeometry(width * inset, shaftH, depth * inset);
  shaft.translate(0, baseH + shaftH * 0.5, 0);
  group.add(new Mesh(shaft, mat));

  const cap = new BoxGeometry(width, baseH, depth);
  cap.translate(0, height - baseH * 0.5, 0);
  group.add(new Mesh(cap, mat));
}

/** Unit-radius cylinder scaled on X/Z so width≠depth yields an elliptical column. */
function addScaledCylinder(
  group: Group,
  rx: number,
  rz: number,
  height: number,
  yCenter: number,
  mat: MeshStandardMaterial,
  radiusTop = 1,
  radiusBottom = 1,
  segments = 36,
): void {
  const geo = new CylinderGeometry(radiusTop, radiusBottom, height, segments);
  geo.translate(0, yCenter, 0);
  const mesh = new Mesh(geo, mat);
  mesh.scale.set(rx, 1, rz);
  group.add(mesh);
}

function addRoundColumn(
  group: Group,
  size: StructuralSizeM,
  color: ColorRepresentation,
): void {
  const height = ROOM_WALL_HEIGHT_M;
  const rx = Math.max(0.025, size.width * 0.5);
  const rz = Math.max(0.025, size.depth * 0.5);
  const mat = makeConcreteMaterial(color);
  const baseH = Math.min(0.07, height * 0.028);
  const shaftInset = 0.9;

  addScaledCylinder(group, rx, rz, baseH, baseH * 0.5, mat);

  const shaftH = height - baseH * 2;
  // Slight taper: wider at floor for a cleaner classical proportion.
  addScaledCylinder(
    group,
    rx * shaftInset,
    rz * shaftInset,
    shaftH,
    baseH + shaftH * 0.5,
    mat,
    0.97,
    1,
  );

  addScaledCylinder(
    group,
    rx * 0.98,
    rz * 0.98,
    baseH,
    height - baseH * 0.5,
    mat,
  );
}

/** Initial / default bottom Y so the beam hangs with its top at room height. */
export function defaultBeamBottomY(heightM: number): number {
  return Math.max(0, ROOM_WALL_HEIGHT_M - Math.max(0.05, heightM));
}

/**
 * Clamp elevatable item bottom Y.
 * Beams stay in [0, roomHeight − h]; pool may go down to −roomHeight.
 */
export function clampBeamBottomY(
  bottomY: number,
  heightM: number,
  kind?: StructuralKind | string | null,
): number {
  const h = Math.max(0.05, heightM);
  const maxBottom = Math.max(0, ROOM_WALL_HEIGHT_M - h);
  const minBottom = allowsNegativeStructuralElevation(kind)
    ? -ROOM_WALL_HEIGHT_M
    : 0;
  return Math.min(maxBottom, Math.max(minBottom, bottomY));
}

function addBeam(
  group: Group,
  size: StructuralSizeM,
  color: ColorRepresentation,
): void {
  const length = Math.max(0.05, size.width);
  const thickness = Math.max(0.05, size.depth);
  const beamH = Math.max(0.05, size.height);
  const mat = makeConcreteMaterial(color);
  // Local origin at beam bottom-center; world Y (`model.position.y`) is elevation.
  // Initial place sets Y so the top sits at the ceiling — then Y is freely adjustable.
  const geo = new BoxGeometry(length, beamH, thickness);
  geo.translate(0, beamH * 0.5, 0);
  group.add(new Mesh(geo, mat));
}

function addRadiator(
  group: Group,
  size: StructuralSizeM,
  color: ColorRepresentation,
): void {
  const { width, depth, height } = size;
  const metal = makeMetalMaterial(color);
  const finMetal = makeMetalMaterial(0xd7dde4);
  const headerH = Math.max(0.035, height * 0.07);
  const headerD = depth * 0.92;

  const top = new BoxGeometry(width, headerH, headerD);
  top.translate(0, height - headerH * 0.5, 0);
  group.add(new Mesh(top, metal));

  const bottom = new BoxGeometry(width, headerH, headerD);
  bottom.translate(0, headerH * 0.5, 0);
  group.add(new Mesh(bottom, metal));

  // Panel fins with air gaps — thin modern radiator look within 15 cm depth.
  const finCount = Math.max(8, Math.round(width / 0.08));
  const sidePad = width * 0.03;
  const usable = Math.max(0.05, width - sidePad * 2);
  const gap = Math.min(0.012, usable * 0.035);
  const finW = (usable - gap * (finCount - 1)) / finCount;
  const finH = Math.max(0.05, height - headerH * 2.15);
  const finD = depth * 0.72;
  const y = headerH + (height - headerH * 2) * 0.5;

  for (let i = 0; i < finCount; i++) {
    const x =
      -width * 0.5 + sidePad + finW * 0.5 + i * (finW + gap);
    const fin = new BoxGeometry(Math.max(0.008, finW), finH, finD);
    fin.translate(x, y, 0);
    group.add(new Mesh(fin, i % 2 === 0 ? metal : finMetal));
  }

  // Slim back plate against the wall (keeps a solid silhouette).
  const back = new BoxGeometry(width * 0.98, finH * 0.96, depth * 0.12);
  back.translate(0, y, -depth * 0.35);
  group.add(new Mesh(back, finMetal));
}

function addPool(group: Group, size: StructuralSizeM): void {
  const width = Math.max(0.6, size.width);
  const depth = Math.max(0.6, size.depth);
  const height = Math.max(0.2, size.height);
  const wallT = Math.min(0.14, Math.min(width, depth) * 0.07);
  const floorH = Math.min(0.08, height * 0.22);
  const rim = makeConcreteMaterial(0xc5d4de);
  const water = makeWaterMaterial(0x5eb0d8);

  const floor = new BoxGeometry(width, floorH, depth);
  floor.translate(0, floorH * 0.5, 0);
  group.add(new Mesh(floor, rim));

  const wallH = height - floorH;
  const wallY = floorH + wallH * 0.5;
  const longWall = new BoxGeometry(width, wallH, wallT);
  const north = new Mesh(longWall, rim);
  north.position.set(0, wallY, depth * 0.5 - wallT * 0.5);
  group.add(north);
  const south = new Mesh(longWall.clone(), rim);
  south.position.set(0, wallY, -(depth * 0.5 - wallT * 0.5));
  group.add(south);

  const shortLen = Math.max(0.1, depth - wallT * 2);
  const shortWall = new BoxGeometry(wallT, wallH, shortLen);
  const east = new Mesh(shortWall, rim);
  east.position.set(width * 0.5 - wallT * 0.5, wallY, 0);
  group.add(east);
  const west = new Mesh(shortWall.clone(), rim);
  west.position.set(-(width * 0.5 - wallT * 0.5), wallY, 0);
  group.add(west);

  const innerW = Math.max(0.2, width - wallT * 2);
  const innerD = Math.max(0.2, depth - wallT * 2);
  const waterH = Math.max(0.04, wallH * 0.72);
  const basin = new BoxGeometry(innerW, waterH, innerD);
  basin.translate(0, floorH + waterH * 0.5, 0);
  group.add(new Mesh(basin, water));
}

/** Build a renderable mesh group; local origin at floor-center, +Y up, +Z forward (into room for wall items). */
export function createStructuralMesh(
  kind: StructuralKind,
  size: StructuralSizeM = structuralItemByKind(kind).defaultSize,
  color?: ColorRepresentation,
): Object3D {
  const def = structuralItemByKind(kind);
  const matColor =
    color ??
    (structuralFollowsWallColor(kind) ? WALL_PAINT : def.color);
  const group = new Group();
  group.name = `structural:${kind}`;

  switch (kind) {
    case "round-column":
      addRoundColumn(group, size, matColor);
      break;
    case "rect-column":
      addRectColumn(group, size, matColor);
      break;
    case "beam":
      addBeam(group, size, matColor);
      break;
    case "radiator":
      addRadiator(group, size, matColor);
      break;
    case "pool":
      addPool(group, size);
      break;
  }

  group.userData.structuralKind = kind;
  return group;
}

export function applyStructuralSizeToData(
  data: ModelData,
  size: StructuralSizeM,
): StructuralSizeM {
  const kind = (data as { structuralKind?: StructuralKind }).structuralKind;
  const next: StructuralSizeM = {
    width: Math.max(0.05, size.width),
    depth: Math.max(0.05, size.depth),
    height: Math.max(0.05, size.height),
  };
  if (isColumnKind(kind)) {
    next.height = ROOM_WALL_HEIGHT_M;
  }
  // Round column: width/depth stay independent (circular when equal, oval when not).
  (data as { structuralSize?: StructuralSizeM }).structuralSize = next;
  data.x = Math.round(next.width * 100);
  data.y = Math.round(next.height * 100);
  data.z = Math.round(next.depth * 100);
  return next;
}
